import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, BookOpen, Radio, MessageSquare, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

export default function NavSearch({ className = "", onNavigate }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); setOpen(false); return; }
    setBusy(true);
    const timer = setTimeout(() => {
      api.get("/search", { params: { q: q.trim() } })
        .then(r => { setRes(r.data); setOpen(true); })
        .catch(() => {})
        .finally(() => setBusy(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const go = (fn) => {
    setOpen(false);
    setQ("");
    fn();
    onNavigate && onNavigate();
  };

  const empty = res && !res.lessons.length && !res.news.length && !res.prompts.length;

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-200 pointer-events-none" aria-hidden="true" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (res) setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
          placeholder="Search lessons, updates, prompts…"
          className="w-full bg-white/10 border border-transparent rounded-full pl-9 pr-3 py-2 text-sm text-white placeholder:text-slate-200 outline-none focus:bg-white/15 focus:border-orange-400/50 focus:ring-2 focus:ring-orange-400/20 transition-colors"
          data-testid="nav-search-input"
        />
      </div>

      {open && (
        <div
          className="absolute top-full mt-2 left-0 right-0 lg:left-auto lg:right-0 lg:w-96 bg-[#0B191E]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto"
          data-testid="search-results"
        >
          {busy && <div className="px-4 py-3 text-sm text-slate-200">Searching…</div>}
          {!busy && empty && <div className="px-4 py-4 text-sm text-slate-200" data-testid="search-no-results">No results found</div>}

          {res?.lessons.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-200 flex items-center gap-1.5">
                <BookOpen size={11} /> Lessons
              </div>
              {res.lessons.map((l) => (
                <button
                  key={l.slug}
                  onClick={() => go(() => navigate(`/lessons/${l.slug}`))}
                  className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors"
                  data-testid={`search-result-lesson-${l.slug}`}
                >
                  <div className="text-sm font-semibold text-white font-bn truncate">{l.title}</div>
                  <div className="text-xs text-slate-200">{l.category}</div>
                </button>
              ))}
            </div>
          )}

          {res?.news.length > 0 && (
            <div className="py-2 border-t border-white/[0.07]">
              <div className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-200 flex items-center gap-1.5">
                <Radio size={11} /> AI Updates
              </div>
              {res.news.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(() => (n.is_article && n.slug ? navigate(`/news/${n.slug}`) : window.open(n.url, "_blank", "noopener")))}
                  className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors flex items-center gap-2"
                  data-testid={`search-result-news-${n.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{n.title}</div>
                    <div className="text-xs text-slate-200">{n.category}</div>
                  </div>
                  <ArrowUpRight size={13} className="text-slate-200 shrink-0" />
                </button>
              ))}
            </div>
          )}

          {res?.prompts.length > 0 && (
            <div className="py-2 border-t border-white/[0.07]">
              <div className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-200 flex items-center gap-1.5">
                <MessageSquare size={11} /> Prompts
              </div>
              {res.prompts.map((p) => (
                <button
                  key={p.slug}
                  onClick={() => go(() => navigate(`/prompts/${p.slug}`))}
                  className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors"
                  data-testid={`search-result-prompt-${p.slug}`}
                >
                  <div className="text-sm font-semibold text-white truncate">{p.title}</div>
                  <div className="text-xs text-slate-200">{p.category}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
